<template>
  <div>
    <v-container fluid>
      <base-material-card
        icon="mdi-clipboard-text"
        title="วุฒิการศึกษาบุคลากรอาชีวศึกษา"
        class="px-5 py-3"
      >
        <!-- ===== ตัวกรอง ===== -->
        <v-row>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.filter_region_ID"
              label="ภาค"
              :items="region"
              item-text="region_name"
              item-value="region_ID"
              clearable
              dense
              @change="onRegionChange"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.filter_province_ID"
              label="จังหวัด"
              :items="filteredProvince"
              item-text="province_name"
              item-value="province_ID"
              clearable
              dense
              @change="reloadAll"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.college_code"
              label="วิทยาลัย"
              :items="college"
              item-text="college_name"
              item-value="college_code"
              clearable
              dense
              @change="reloadAll"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.education_level"
              label="ระดับการศึกษา"
              :items="education_level"
              clearable
              dense
              @change="reloadAll"
            ></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="filters.search"
              append-icon="mdi-magnify"
              label="ค้นหา ชื่อ-สกุล / วุฒิ / สาขา / สถานศึกษา / วิทยาลัย"
              single-line
              hide-details
              dense
              filled
              @keyup.enter="reloadAll"
              @click:append="reloadAll"
            />
          </v-col>
          <v-col cols="12" md="4" class="text-right">
            <v-btn
              color="success"
              outlined
              :loading="exporting"
              @click="exportExcel"
            >
              <v-icon left>mdi-file-excel</v-icon>
              ส่งออก Excel (ตามตัวกรองปัจจุบัน)
            </v-btn>
          </v-col>
        </v-row>
      </base-material-card>

      <!-- ===== Dashboard ===== -->
      <v-row class="mt-2">
        <v-col cols="12" md="3" v-for="card in summaryCards" :key="card.label">
          <v-card class="pa-4 d-flex align-center" outlined>
            <v-icon large :color="card.color" class="mr-3">{{
              card.icon
            }}</v-icon>
            <div>
              <div class="text-h5 font-weight-bold">{{ card.value }}</div>
              <div class="grey--text">{{ card.label }}</div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-card class="pa-3" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">
              สัดส่วนตามระดับการศึกษา
            </div>
            <apexchart
              v-if="chartByLevel.series.length"
              type="donut"
              height="320"
              :options="chartByLevel.options"
              :series="chartByLevel.series"
            ></apexchart>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูล</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="pa-3" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">
              จำนวนบุคลากรตามภาค
            </div>
            <apexchart
              v-if="
                chartByRegion.series[0] && chartByRegion.series[0].data.length
              "
              type="bar"
              height="320"
              :options="chartByRegion.options"
              :series="chartByRegion.series"
            ></apexchart>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูล</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-card class="pa-3" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">Top 10 สาขาวิชา</div>
            <apexchart
              v-if="
                chartTopBranch.series[0] && chartTopBranch.series[0].data.length
              "
              type="bar"
              height="360"
              :options="chartTopBranch.options"
              :series="chartTopBranch.series"
            ></apexchart>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูล</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="pa-3" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">
              Top 10 วิทยาลัย (จำนวนวุฒิที่บันทึก)
            </div>
            <apexchart
              v-if="
                chartTopCollege.series[0] &&
                  chartTopCollege.series[0].data.length
              "
              type="bar"
              height="360"
              :options="chartTopCollege.options"
              :series="chartTopCollege.series"
            ></apexchart>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูล</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== ตารางข้อมูล (server-side paging) ===== -->
      <base-material-card
        icon="mdi-table"
        title="รายการข้อมูล"
        class="px-5 py-3 mt-4"
      >
        <v-data-table
          :loading="loading"
          :headers="headers"
          :items="rows"
          :items-per-page="filters.per_page"
          :server-items-length="total"
          :options.sync="tableOptions"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
          loading-text="กำลังโหลดข้อมูล..."
          no-data-text="ไม่พบข้อมูล"
        >
          <template v-slot:item.fullname="{ item }">
            {{ item.title_s }}{{ item.frist_name }} {{ item.last_name }}
          </template>
        </v-data-table>
      </base-material-card>
    </v-container>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="4000"
      :color="snackbar.color"
      top
      right
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import VueApexCharts from "vue-apexcharts";

export default {
  name: "PersonnelEducationAdmin",
  components: { apexchart: VueApexCharts },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      exporting: false,
      total: 0,
      rows: [],
      college: [],
      region: [],
      province: [],
      education_level: ["ต่ำกว่า ป.ตรี", "ปริญญาตรี", "ปริญญาโท", "ปริญญาเอก"],

      filters: {
        search: "",
        college_code: null,
        education_level: null,
        filter_province_ID: null,
        filter_region_ID: null,
        page: 1,
        per_page: 25
      },

      tableOptions: {},

      headers: [
        { text: "วิทยาลัย", align: "left", value: "college_name" },
        { text: "จังหวัด", align: "left", value: "province_name" },
        { text: "ภาค", align: "left", value: "region_name" },
        {
          text: "ชื่อ-สกุล",
          align: "left",
          value: "fullname",
          sortable: false
        },
        { text: "ระดับการศึกษา", align: "left", value: "education_level" },
        { text: "วุฒิ", align: "left", value: "faculty_name" },
        { text: "สาขาวิชา", align: "left", value: "branch_name" },
        { text: "จบจาก", align: "left", value: "academy_name" },
        { text: "ปีที่จบ", align: "center", value: "year_finish" },
        { text: "GPA", align: "center", value: "academic_results" }
      ],

      chartByLevel: { series: [], options: {} },
      chartByRegion: { series: [{ name: "จำนวน", data: [] }], options: {} },
      chartTopBranch: { series: [{ name: "จำนวน", data: [] }], options: {} },
      chartTopCollege: { series: [{ name: "จำนวน", data: [] }], options: {} },

      summaryCards: [],

      snackbar: { show: false, color: "", text: "" }
    };
  },

  computed: {
    filteredProvince() {
      if (!this.filters.filter_region_ID) return this.province;
      return this.province.filter(
        p => p.region_ID === this.filters.filter_region_ID
      );
    }
  },

  watch: {
    tableOptions: {
      handler(opt) {
        this.filters.page = opt.page || 1;
        this.filters.per_page = opt.itemsPerPage || 25;
        this.loadList();
      },
      deep: true
    }
  },

  async mounted() {
    await Promise.all([this.loadCollege(), this.loadRegionProvince()]);
    await this.reloadAll();
  },

  methods: {
    onRegionChange() {
      this.filters.filter_province_ID = null;
      this.reloadAll();
    },

    async reloadAll() {
      this.filters.page = 1;
      await Promise.all([this.loadList(), this.loadStats()]);
    },

    async loadCollege() {
      let result = await this.$http.post("college.php", {
        ApiKey: this.ApiKey
      });
      this.college = result.data;
    },

    async loadRegionProvince() {
      // ปรับ endpoint ตามที่ระบบมีจริง (region.php / province.php)
      let [regionRes, provinceRes] = await Promise.all([
        this.$http.post("region.php", { ApiKey: this.ApiKey }),
        this.$http.post("province.php", { ApiKey: this.ApiKey })
      ]);
      this.region = regionRes.data;
      this.province = provinceRes.data;
    },

    async loadList() {
      this.loading = true;
      try {
        let result = await this.$http.post("personnel_education_admin.php", {
          ApiKey: this.ApiKey,
          action: "list",
          ...this.filters
        });
        this.rows = result.data.data || [];
        this.total = result.data.total || 0;
      } catch (e) {
        this.showSnackbar("error", "โหลดข้อมูลไม่สำเร็จ");
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      try {
        let result = await this.$http.post("personnel_education_admin.php", {
          ApiKey: this.ApiKey,
          action: "stats",
          ...this.filters
        });
        const d = result.data;

        // การ์ดสรุป
        const totalAll = (d.by_level || []).reduce(
          (s, r) => s + Number(r.total),
          0
        );
        this.summaryCards = [
          {
            label: "วุฒิทั้งหมด",
            value: totalAll,
            icon: "mdi-certificate",
            color: "indigo"
          },
          {
            label: "ปริญญาตรี",
            value: this.pickLevel(d.by_level, "ปริญญาตรี"),
            icon: "mdi-school",
            color: "blue"
          },
          {
            label: "ปริญญาโท",
            value: this.pickLevel(d.by_level, "ปริญญาโท"),
            icon: "mdi-trophy-award",
            color: "deep-purple"
          },
          {
            label: "ปริญญาเอก",
            value: this.pickLevel(d.by_level, "ปริญญาเอก"),
            icon: "mdi-star-circle",
            color: "pink"
          }
        ];

        // Donut: ระดับการศึกษา
        this.chartByLevel = {
          series: (d.by_level || []).map(r => Number(r.total)),
          options: {
            labels: (d.by_level || []).map(r => r.label || "ไม่ระบุ"),
            legend: { position: "bottom" },
            colors: ["#5c6bc0", "#7e57c2", "#ec407a", "#78909c", "#26a69a"],
            dataLabels: { enabled: true }
          }
        };

        // Bar: ภาค
        this.chartByRegion = {
          series: [
            {
              name: "จำนวน",
              data: (d.by_region || []).map(r => Number(r.total))
            }
          ],
          options: {
            chart: { toolbar: { show: false } },
            xaxis: {
              categories: (d.by_region || []).map(r => r.label || "ไม่ระบุ")
            },
            plotOptions: { bar: { borderRadius: 4, horizontal: false } },
            colors: ["#5c6bc0"],
            dataLabels: { enabled: false }
          }
        };

        // Bar: Top branch
        this.chartTopBranch = {
          series: [
            {
              name: "จำนวน",
              data: (d.top_branch || []).map(r => Number(r.total))
            }
          ],
          options: {
            chart: { toolbar: { show: false } },
            xaxis: {
              categories: (d.top_branch || []).map(r => r.label || "ไม่ระบุ")
            },
            plotOptions: { bar: { borderRadius: 4, horizontal: true } },
            colors: ["#43a047"],
            dataLabels: { enabled: false }
          }
        };

        // Bar: Top college
        this.chartTopCollege = {
          series: [
            {
              name: "จำนวน",
              data: (d.by_college || []).map(r => Number(r.total))
            }
          ],
          options: {
            chart: { toolbar: { show: false } },
            xaxis: {
              categories: (d.by_college || []).map(r => r.label || "ไม่ระบุ")
            },
            plotOptions: { bar: { borderRadius: 4, horizontal: true } },
            colors: ["#fb8c00"],
            dataLabels: { enabled: false }
          }
        };
      } catch (e) {
        this.showSnackbar("error", "โหลดข้อมูลกราฟไม่สำเร็จ");
      }
    },

    pickLevel(arr, label) {
      const found = (arr || []).find(r => r.label === label);
      return found ? Number(found.total) : 0;
    },

    async exportExcel() {
      this.exporting = true;
      try {
        // ดึงข้อมูลทั้งหมดตามตัวกรองปัจจุบัน (ไม่จำกัดหน้า) แยกจากตารางที่แสดงผล
        let result = await this.$http.post("personnel_education_admin.php", {
          ApiKey: this.ApiKey,
          action: "export",
          search: this.filters.search,
          college_code: this.filters.college_code,
          education_level: this.filters.education_level,
          filter_province_ID: this.filters.filter_province_ID,
          filter_region_ID: this.filters.filter_region_ID
        });

        const rows = result.data.data || [];
        if (!rows.length) {
          this.showSnackbar("warning", "ไม่มีข้อมูลให้ส่งออกตามตัวกรองนี้");
          return;
        }

        const exportData = rows.map(item => ({
          วิทยาลัย: item.college_name,
          จังหวัด: item.province_name,
          ภาค: item.region_name,
          คำนำหน้าชื่อ: item.title_s,
          ชื่อ: item.frist_name,
          นามสกุล: item.last_name,
          ระดับการศึกษา: item.education_level,
          วุฒิ: item.faculty_name,
          สาขาวิชา: item.branch_name,
          จบจาก: item.academy_name,
          ปีที่จบ: item.year_finish,
          ผลการเรียน: item.academic_results
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "วุฒิการศึกษา");
        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(
          new Blob([wbout], { type: "application/octet-stream" }),
          `วุฒิการศึกษา_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
      } catch (e) {
        this.showSnackbar("error", "ส่งออกข้อมูลไม่สำเร็จ");
      } finally {
        this.exporting = false;
      }
    },

    showSnackbar(color, text) {
      this.snackbar = { show: true, color, text };
    }
  }
};
</script>
